import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Box, Image } from "@chakra-ui/react";
import { getAppValue } from "src/utils/environment";
import { H1, H3, BodyText } from "src/atoms/typography";
import CloseButton from "src/atoms/CloseButton";
import { Lottie } from "src/components/Lottie";
import getImageSrc from "src/utils/getImageSrc";
import SvgContainer from "src/components/SvgContainer";
import { ReactComponent as Improvements } from "./icons/improvements.svg";
import { ReactComponent as QR } from "./icons/qr.svg";
import { ReactComponent as UI } from "./icons/ui.svg";
import { ReactComponent as Alerts } from "./icons/alerts.svg";

// CURRENT CONTENT VERSION = { Barkio: 4, Bibino: 3 }
// change content version in ./version-control to trigger what's new
const content = [
  {
    title: "whatsNew.enhancedInterface.title",
    subtitle: "whatsNew.enhancedInterface.subtitle",
    Icon: UI,
    iconFill: getAppValue({ barkio: "primary", bibino: "tintTurquoise" }),
    iconBg: getAppValue({
      barkio: "primaryLight",
      bibino: "tintTurquoiseLight"
    })
  },
  {
    title: "whatsNew.qrPairing.title",
    subtitle: "whatsNew.qrPairing.subtitle",
    Icon: QR,
    iconFill: "warningBase",
    iconBg: "warningLight"
  },
  {
    title: "whatsNew.connectionErrors.title",
    subtitle: "whatsNew.connectionErrors.subtitle",
    Icon: Alerts,
    iconFill: getAppValue({ barkio: "tintBlue", bibino: "tintPurple" }),
    iconBg: getAppValue({
      barkio: "tintBlueLight",
      bibino: "tintPurpleLight"
    })
  },
  {
    title: "whatsNew.improvements.title",
    subtitle: "whatsNew.improvements.subtitle",
    Icon: Improvements,
    iconFill: getAppValue({ barkio: "tint2", bibino: "tintYellow" }),
    iconBg: getAppValue({
      barkio: "tint2Light",
      bibino: "tintYellowLight"
    })
  }
];

const Svg = ({ Icon, fill, bg, ...rest }) => (
  <Flex mb="16px" alignItems="center" justifyContent="center" p="12px" borderRadius="100%" bg={bg} {...rest}>
    <SvgContainer fill={fill} icon={Icon} />
  </Flex>
);

export default function WhatsNew({ onClose }) {
  const [videos, setVideos] = useState([]);
  const { t } = useTranslation();

  return (
    <>
      <CloseButton onClick={onClose} absolute />
      <Box width="100%" height="100%" position="relative" overflowY="auto">
        <Flex mt="32px" px="86px" flexDirection="column" alignItems="center">
          <H1 color="highEmphasis" mb="8px">
            {t("whatsNew.title")}
          </H1>
          <BodyText color="mediumEmphasis" textAlign="center" mb="32px">
            {t("whatsNew.subtitle")}
          </BodyText>
          {content.map(({ title, subtitle, Icon, iconFill, iconBg, animation, video, thumbnail }, i) => {
            return (
              <Flex key={i} flexDirection="column" alignItems="center">
                {Icon && <Svg Icon={Icon} fill={iconFill} bg={iconBg} />}
                {animation && (
                  <Lottie
                    mb="16px"
                    width={getAppValue({
                      bibino: "260px",
                      barkio: "198px"
                    })}
                    height={getAppValue({
                      bibino: "102px",
                      barkio: "136px"
                    })}
                    animationData={animation}
                  />
                )}
                {video &&
                  (videos.includes(i) ? (
                    <Box mb="32px">
                      <iframe
                        title="whats new video"
                        src={video}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        frameBorder="0"
                        width="368"
                        height="207"
                        style={{ borderRadius: "16px" }}
                      />
                    </Box>
                  ) : (
                    <Image
                      mb="32px"
                      width="368"
                      height="207"
                      borderRadius="16px"
                      _hover={{
                        cursor: "pointer"
                      }}
                      onClick={() => setVideos((prevState) => [...prevState, i])}
                      src={getImageSrc(thumbnail, {
                        directory: "whats-new"
                      })}
                    />
                  ))}
                {title && (
                  <H3 color="highEmphasis" mb="8px">
                    {t(title)}
                  </H3>
                )}
                {subtitle && (
                  <BodyText color="mediumEmphasis" textAlign="center" mb="32px">
                    {t(subtitle)}
                  </BodyText>
                )}
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </>
  );
}
